<template>
  <div class="acc-shell">
    <nav class="acc-tabs">
      <router-link
        v-for="item in visibleItems"
        :key="item.to"
        :to="item.to"
        class="acc-tab"
        active-class="is-active"
      >
        <span class="tab-icon" v-html="item.icon"></span>
        <span class="tab-label">{{ item.label }}</span>
      </router-link>
    </nav>

    <main class="acc-main">
      <router-view />
    </main>
  </div>
</template>

<script>
import http from "../../http-common";

const SECURITY_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 12l2 2 4-4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const KEY_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="8" cy="15" r="4"/><path d="M10.85 12.15L19 4M18 5l3 3M15 8l3 3" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const LEDGER_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 10h18M8 4v16" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const USERS_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

export default {
  name: "account-hub",
  data() {
    return {
      // Tri-state so we don't flash the tab in for a frame while the
      // opening-balance check is in flight for branch/client roles:
      //   null  — check not run yet (or role doesn't need one)
      //   true  — opening balance exists → Ledger tab shows
      //   false — no opening balance → tab stays hidden
      // Vendors (superadmin / admin+canManageClients) bypass this entirely
      // and always see the tab.
      hasOpeningBalance: null,
    };
  },
  computed: {
    isSuperAdmin() { return !!this.$store.getters.isSuperAdmin; },
    isAdmin()      { return !!this.$store.getters.isAdmin; },
    isSubAdmin()   { return !!this.$store.getters.isSubAdmin; },
    isClient()     { return !!this.$store.getters.isClient; },
    canManageClients() { return !!this.$store.getters.canManageClients; },

    showPassword() {
      // Every role — including clients — can change their OWN password
      // (self-service; backend verifies the current password and acts on
      // req.user). The tab lives next to Security in the account hub.
      return true;
    },
    showLedger() {
      // Vendor tier — always on (their view lists every entity).
      if (this.isSuperAdmin) return true;
      if (this.isAdmin && this.canManageClients) return true;
      // Self-view for branch (subadmin) + client — only when ops has
      // posted an opening balance for THIS user's own entity. Per the
      // v1 ledger design, an entity without an opening balance has no
      // ledger at all (cron skips it), so surfacing the tab would land
      // them on a permanently-empty page.
      if (this.isSubAdmin || this.isClient) return this.hasOpeningBalance === true;
      return false;
    },

    visibleItems() {
      const items = [
        { to: "/account/security", label: "Security", icon: SECURITY_ICON },
      ];
      if (this.showPassword) {
        items.push({ to: "/account/password", label: "Password", icon: KEY_ICON });
      }
      if (this.showLedger) {
        items.push({ to: "/account/ledger", label: "Ledger", icon: LEDGER_ICON });
      }
      // Supported Users — menu shown ONLY to vendor (admin) + superadmin
      // (11102026 product decision). Hidden for client / branch (subadmin) /
      // bank (user) / order-creator / support, and from supporting users
      // themselves (one-level rule). isSupporting/role come from the JWT/store.
      if (!this.isSupporting && (this.isAdmin || this.isSuperAdmin)) {
        items.push({ to: "/account/supported-users", label: "Supported Users", icon: USERS_ICON });
      }
      return items;
    },
    isSupporting() { return !!this.$store.getters.isSupporting; },
  },
  mounted() {
    // Only branch/client need the async check — vendor tiers already
    // see the tab unconditionally via showLedger short-circuits above.
    if (this.isSubAdmin || this.isClient) {
      this.checkOpeningBalance();
    }
  },
  methods: {
    /*
      GET /api/v1/ledger/balance/:entity_type/:entity_id
      For subadmin/client the backend force-scopes both params to the
      signed-in user's own row (see controllers/ledger/getBalance.js),
      so the values we pass in the URL are placeholders — but the URL
      still needs a well-formed entity_type + numeric id for routing.
      `unsettledWindowStart` is null iff no opening balance exists,
      per the v1 balance calculator; that's the single signal we key on.
      On failure we keep the tab hidden — safer than surfacing an empty
      ledger the user can't do anything about.
    */
    async checkOpeningBalance() {
      const entityType = this.isSubAdmin ? "branch" : "client";
      const entityId = this.$store.getters.id || this.$store.getters.userId || 0;
      try {
        const res = await http.get(`/ledger/balance/${entityType}/${entityId}`);
        const data = res && res.data;
        this.hasOpeningBalance = !!(data && data.unsettledWindowStart);
      } catch (_e) {
        this.hasOpeningBalance = false;
      }
    },
  },
};
</script>

<style scoped>
.acc-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #ffffff;
}

/* ==================== Tab bar ==================== */
.acc-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 24px 0;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  overflow-x: auto;
  flex-shrink: 0;
}

.acc-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px 10px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  text-decoration: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color 0.15s, background 0.15s, border-color 0.15s;
  white-space: nowrap;
  position: relative;
  top: 1px;
}
.acc-tab:hover {
  color: #1B2A47;
  background: #f8fafc;
}
.acc-tab.is-active {
  color: #1B2A47;
  border-bottom-color: #1B2A47;
  background: transparent;
}

.tab-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: currentColor;
  opacity: 0.75;
  transition: opacity 0.15s;
}
.acc-tab:hover .tab-icon,
.acc-tab.is-active .tab-icon {
  opacity: 1;
}
.tab-icon :deep(svg) { width: 16px; height: 16px; }

.tab-label { line-height: 1; }

/* ==================== Main area ==================== */
.acc-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.acc-main > * {
  flex: 1;
  min-height: 0;
}

/* ==================== Responsive ==================== */
@media (max-width: 640px) {
  .acc-tabs {
    padding: 8px 12px 0;
    gap: 2px;
  }
  .acc-tab {
    padding: 10px 12px;
    font-size: 12.5px;
  }
}
</style>
